import { getLogger } from "./util/logger";
import { PersistentConnection } from "./util/signalr-helpers";
import { GamePlanLoggingHub, IGamePlanLoggingHub } from "./util/signalr-logging-hub";
import { formatDate, logToDevSqlite } from "./dev-logging";

interface LoggingSetup {
    loggingKey: string;
    loggingId: string;
}

const setup: LoggingSetup = {
    loggingId: "",
    loggingKey: "",
};

let logToConsole = false;
let logToServer = true;
// In local dev, logs are also mirrored to the DevServer's SQLite sink so
// the terminal can `sqlite3 gameplan-debug.db ...` to tail — that flag
// (logToDevSqlite) and recordDevLog live in dev-logging.ts, which is
// import-safe outside a browser (this module's $() bootstrap is not).

if (typeof (__gameplan_is_local_development) !== 'undefined' && __gameplan_is_local_development) {
    logToConsole = true;
    logToServer = false;
}

export function configureLogs() {
    // look for the special logging setup command
    if (typeof (__gpLogs) !== "undefined" && __gpLogs && __gpLogs.length > 0) {
        const logsToSend = __gpLogs;

        __gpLogs = [];

        logsToSend.forEach(parts => {
            let when: Date = parts[0];
            let msgTemplate: string = parts[1];

            if (msgTemplate === "gpLogSetup") {
                const setupArgs: LoggingSetup = parts[2];

                setup.loggingId = setupArgs.loggingId;
                setup.loggingKey = setupArgs.loggingKey;

            } else {
                // if it's not a setup command, put the message back for posting.
                __gpLogs.push(parts);
            }
        });
    }
}

let signalrConnection: PersistentConnection | null = null;
let loggingHub: IGamePlanLoggingHub | null;
function gameplanSignalrConnection() {

    if (!signalrConnection) {

        signalrConnection = new PersistentConnection('logging', '', (conn) => {
            loggingHub = new GamePlanLoggingHub(conn);
        })
    }

    return signalrConnection;
}

export function sendLogs() {
    if (!(typeof (__gpLogs) !== "undefined" && __gpLogs && __gpLogs.length > 0)) {
        return;
    }
    const logsToSend = __gpLogs;
    __gpLogs = [];

    if (logToConsole) {
        logsToSend.forEach(parts => {
            const when: Date = parts[0];
            const msgTemplate: string = parts[1];
            const args: any[] = parts[2];
            const extras: any = parts[3];
            getLogger('general').debug(msgTemplate, args, extras ? JSON.stringify(extras, null, '\t') : "");
        });
    }

    if (logToServer) {

        const connection = gameplanSignalrConnection();

        connection.ensureConnection()
            .done(() => {
                let hub = loggingHub;

                logsToSend.forEach(parts => {
                    if (!hub) return;
                    const when: Date = parts[0];
                    const msgTemplate: string = parts[1];
                    const args: any[] = parts[2];
                    const extras: any = parts[3];

                    const props: any = { clientTime: formatDate(when) };

                    if (extras) {
                        for (let k in extras) {
                            props[k] = extras[k];
                        }
                    }

                    hub.information(setup.loggingKey, msgTemplate, args, props);
                });
            });
    }

    if (logToDevSqlite) {
        // Fire-and-forget POST to the DevServer's SQLite sink.
        // Failures don't surface — the console mirror above is the
        // dev's primary feedback channel.
        const entries = logsToSend.map(parts => {
            const when: Date = parts[0];
            const msgTemplate: string = parts[1];
            const args: any[] = parts[2];
            const extras: any = parts[3];
            // Extras often carries a "ClientContext" field that names the
            // module/feature (see jsloggly-service.ts) — promote it to
            // the category column. Plan id, if present in extras, lifts
            // out into its own column for cheap filtering.
            const category = extras?.ClientContext ?? extras?.category ?? null;
            const planId = extras?.planId ?? extras?.PlanId ?? null;
            const message = renderTemplate(msgTemplate, args);
            const payload = extras ? JSON.stringify(extras) : null;
            return {
                sourceTime: formatDate(when),
                source: 'browser',
                category,
                planId,
                level: 'info',
                message,
                payloadJson: payload,
            };
        });
        try {
            fetch('/api/dev-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ entries }),
                // No credentials needed — endpoint is unauthenticated in dev.
            }).catch(() => { /* swallow */ });
        } catch { /* swallow */ }
    }
}

/**
 * Trivial {0}/{1} index substitution + {Name} keyed substitution against
 * the args array. Mirrors how `getLogger().information(msg, args)`
 * displays them in the console. Not a full Serilog templater — best-
 * effort dev-time rendering.
 */
function renderTemplate(template: string, args: any[]): string {
    if (!args || args.length === 0) return template;
    let i = 0;
    return template.replace(/\{(\w+)\}/g, (_match, _name) => {
        const v = args[i++];
        if (v === undefined) return _match;
        try { return typeof v === 'string' ? v : JSON.stringify(v); }
        catch { return String(v); }
    });
}

$(() => {

    function sendLogsAndSetTimer() {
        sendLogs();
        setTimeout(sendLogsAndSetTimer, 1000);
    };

    configureLogs();
    setTimeout(sendLogsAndSetTimer, 1000);

});
